import _ from 'lodash';
import { useCallback, useEffect, useRef, useState } from 'react';
import { AsyncTypeahead, Highlighter } from 'react-bootstrap-typeahead';
import { useTranslation } from 'react-i18next';
import { DoneLoadingIndicator } from '@/core/DoneLoadingIndicator.atom';
import { HoverTooltip } from '@/core/HoverTooltip.atom';
import { focusAndSelectElement } from '@/utilities/dom.utilities';
import { sqUsersApi } from '@seeqdev/api/endpoint/UsersApi';
import { Icon, TextField } from '@seeqdev/qomponents';
import type { RenderMenuItemChildren } from 'react-bootstrap-typeahead/types/components/TypeaheadMenu';
import type Typeahead from 'react-bootstrap-typeahead/types/core/Typeahead';
import type { IdentityPreviewV1 } from '@seeqdev/api/model/IdentityPreviewV1';

export type IdentityOption = Pick<IdentityPreviewV1, 'id' | 'name' | 'username' | 'email' | 'datasource'> & {
  customOption?: boolean;
  label?: string;
  isOrganizationGroup?: boolean;
};

type IdentityOptionType<T> = T extends true ? IdentityOption[] : IdentityOption;

interface SelectIdentityProps<T> {
  idForLabel?: string;
  setIdentity: (object: IdentityOptionType<T>) => void;
  onIdentityChange?: (identity: IdentityOptionType<T>) => void;
  identity: IdentityOptionType<T>;
  allowGroups?: boolean;
  showDirectory?: boolean;
  includeAllProperties?: boolean;
  startEditable?: boolean;
  placeholder?: string;
  tooltip?: string;
  isInvalid?: boolean;
  unauthorizedTooltip?: string;
  multiple?: T;
  clearIdentityWhenEmpty?: boolean;
  allowNew?: boolean;
  allowEditing?: boolean;
  maxHeight?: string;
  positionFixed?: boolean;
  skipIdentity?: string | null;
  usePortal?: boolean;
  removeEditIcon?: boolean;
  includeExternalOrgUsers?: boolean;
  includeExternalOrgGroups?: boolean;
  includeExternalOrgUsersFromDatasourceClass?: string;
  includeExternalOrgUsersFromDatasourceId?: string;
}

/** Input field for searching and selecting Seeq Identities (Users or Groups) */
export const SelectIdentity = <T extends boolean = false>(props: SelectIdentityProps<T>) => {
  const {
    setIdentity,
    onIdentityChange,
    identity,
    placeholder,
    tooltip,
    unauthorizedTooltip,
    idForLabel,
    allowGroups = false,
    showDirectory = false,
    includeAllProperties = false,
    startEditable = false,
    multiple = false,
    isInvalid,
    clearIdentityWhenEmpty = false,
    allowNew = false,
    allowEditing = false,
    maxHeight = undefined,
    positionFixed = false,
    skipIdentity = null,
    usePortal = false,
    removeEditIcon = false,
    includeExternalOrgUsers,
    includeExternalOrgGroups,
    includeExternalOrgUsersFromDatasourceClass,
    includeExternalOrgUsersFromDatasourceId,
  } = props;
  const { t } = useTranslation();

  const [loadingIdentities, setLoadingIdentities] = useState(false);
  const [options, setOptions] = useState<IdentityOption[]>([]);
  const [editingIdentity, setEditingIdentity] = useState(startEditable);
  const ref = useRef<Typeahead>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const selectedIdentities = _.reject(_.castArray(identity), _.isEmpty);

  useEffect(() => {
    if (_.isEmpty(identity) && clearIdentityWhenEmpty && !loadingIdentities) {
      ref.current?.clear();
    }
  }, [identity]);

  // Scroll to show newly added tokens
  useEffect(() => {
    if (containerRef.current && selectedIdentities.length > 0) {
      const tokenContainer = containerRef.current.querySelector('.rbt-input-multi');
      if (tokenContainer) {
        tokenContainer.scrollTop = tokenContainer.scrollHeight;
      }
    }
  }, [selectedIdentities.length]);

  const renderMenu: RenderMenuItemChildren = (option, props) => {
    const castedOption = option as Record<string, any>;

    return (
      <div className="acl-user-menu-options flexColumnContainer">
        {castedOption.type === 'User' ? (
          <Icon icon="fa-user" extraClassNames="width-18 icon-hover" type="text" />
        ) : null}
        {castedOption.type === 'UserGroup' && castedOption.isOrganizationGroup ? (
          <Icon icon="fa-industry" extraClassNames="width-18 icon-hover" type="text" />
        ) : null}
        {castedOption.type === 'UserGroup' && !castedOption.isOrganizationGroup ? (
          <Icon icon="fa-user-group" extraClassNames="width-18 icon-hover" type="text" />
        ) : null}
        <div>
          <Highlighter search={props.text}>{castedOption.name}</Highlighter>
          <br />
          {castedOption.type === 'User' ? <small>({castedOption.email || castedOption.username})</small> : null}
          {showDirectory ? <small>{`${t('ADMIN.USER.DIRECTORY')}: ${castedOption.datasource?.name}`}</small> : null}
        </div>
      </div>
    );
  };

  const handleIdentitiesSearch = useCallback(
    (query: string) => {
      setLoadingIdentities(true);

      return (
        sqUsersApi
          .autocompleteUsersAndGroups({
            query,
            ...(includeExternalOrgUsers !== undefined && { includeExternalOrgUsers }),
            ...(includeExternalOrgGroups !== undefined && { includeExternalOrgGroups }),
            ...(includeExternalOrgUsersFromDatasourceClass !== undefined && {
              includeExternalOrgUsersFromDatasourceClass,
            }),
            ...(includeExternalOrgUsersFromDatasourceId !== undefined && {
              includeExternalOrgUsersFromDatasourceId,
            }),
          })
          // Filter out user groups
          .then(({ data: { items } }) => {
            const usersAndGroups = allowGroups ? (items as IdentityPreviewV1[]) : _.filter(items, { type: 'User' });
            const filtered: IdentityOption[] = includeAllProperties
              ? usersAndGroups
              : _.map(usersAndGroups, (userOrGroup) =>
                  _.pick(userOrGroup, ['id', 'name', 'username', 'type', 'email', 'datasource', 'isOrganizationGroup']),
                );

            if (skipIdentity) {
              const skip = _.find(filtered, { id: skipIdentity });
              _.pull(filtered, skip);
            }

            setOptions(filtered);
          })
          .finally(() => {
            setLoadingIdentities(false);
          })
      );
    },
    [
      allowGroups,
      includeAllProperties,
      skipIdentity,
      includeExternalOrgUsers,
      includeExternalOrgGroups,
      includeExternalOrgUsersFromDatasourceClass,
      includeExternalOrgUsersFromDatasourceId,
    ],
  );

  const onChange = (selected: IdentityOptionType<T> | IdentityOptionType<T>[]) => {
    if (multiple) {
      const castedSelected = selected as IdentityOptionType<T>;
      setIdentity(castedSelected);
      onIdentityChange?.(castedSelected);
    } else {
      const castedSelected = selected as IdentityOptionType<T>[];
      const selectedIdentity = (castedSelected.length === 1 ? _.first(castedSelected) : {}) as IdentityOptionType<T>;
      setIdentity(selectedIdentity);
      onIdentityChange?.(selectedIdentity);
    }
  };

  const editIdentity = () => {
    setEditingIdentity(true);
    setIdentity({} as IdentityOptionType<T>);
  };

  const renderIdentity = (option: Record<string, any>): string => {
    if (_.isNil(option)) {
      return '';
    }

    if (option?.customOption) {
      return option.label ?? '';
    }

    if (option?.isOrganizationGroup) {
      return `${option.name} (${t('ACCESS_CONTROL.EXTERNAL')})`;
    } else if (option?.email) {
      return `${option.name} (${option.email})`;
    } else if (option?.username) {
      return `${option.name} (${option.username})`;
    } else {
      return option?.name;
    }
  };

  const renderEditable = (
    <div
      ref={containerRef}
      className={`tw:flex selectIdentity tw:w-full tw:min-w-0 input-group ${usePortal ? 'selectIdentity-portal' : ''}`}
      data-testid={`selectIdentityInput_${idForLabel}`}>
      {loadingIdentities && <DoneLoadingIndicator />}
      <AsyncTypeahead
        id={idForLabel}
        ref={ref}
        flip={true}
        labelKey={(option) => renderIdentity(option as Record<string, any>)}
        allowNew={allowNew}
        multiple={multiple}
        options={options}
        isLoading={loadingIdentities}
        filterBy={(option) => !_.some(selectedIdentities, { id: (option as Record<string, any>).id })}
        onSearch={handleIdentitiesSearch}
        onChange={(value) => onChange(value as IdentityOptionType<T> | IdentityOptionType<T>[])}
        renderMenuItemChildren={renderMenu}
        placeholder={t(placeholder ?? '')}
        selected={selectedIdentities}
        isInvalid={isInvalid}
        positionFixed={positionFixed || usePortal}
        maxHeight={maxHeight}
        inputProps={{ className: 'tw:w-full tw:text-sq-text-color' }}
        // Add custom class to tokenizer for vertical stacking
        className="tw:w-full rbt-tokenizer-vertical"
      />
    </div>
  );

  const renderDisplayMode = (
    <div className="tw:flex tw:w-full tw:whitespace-nowrap" data-testid={`selectIdentityViewMode_${idForLabel}`}>
      <TextField
        id={idForLabel}
        type="text"
        extraClassNames={`displayFlex tableWrapper ${renderIdentity(identity) ? 'btrr0 bbrr0' : ''}`}
        disabled={true}
        onFocus={focusAndSelectElement}
        value={renderIdentity(identity)}
      />
      {!removeEditIcon && (
        <span
          className="sq-btn tw:flex tw:items-center tw:cursor-pointer tw:text-sm"
          data-testid={`selectIdentityEditIdentity_${idForLabel}`}
          onClick={editIdentity}>
          <Icon icon="fc-edit" type="text" tooltip={t(tooltip ?? '')} />
        </span>
      )}
    </div>
  );

  const renderViewOnly = (
    <div
      className="tw:flex tw:w-full tw:whitespace-nowrap input-group"
      data-testid={`selectIdentityViewMode_${idForLabel}`}>
      <HoverTooltip text={unauthorizedTooltip} placement="top">
        <TextField
          id={idForLabel}
          type="text"
          readonly={true}
          onFocus={focusAndSelectElement}
          value={renderIdentity(identity)}
        />
      </HoverTooltip>
    </div>
  );

  if (editingIdentity) {
    return renderEditable;
  } else if (allowEditing) {
    return renderDisplayMode;
  } else {
    return renderViewOnly;
  }
};
